<template>
  <div class="space-y-3">
    <svg
      ref="svgEl"
      class="h-[210px] w-full min-w-0 touch-none select-none"
      :viewBox="`0 0 ${width} ${height}`"
      aria-label="ADSR envelope editor"
      @pointermove="moveHandle"
      @pointerup="endDrag"
      @pointercancel="endDrag"
    >
      <defs>
        <linearGradient id="adsr-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#2dd4bf" stop-opacity="0.32" />
          <stop offset="55%" stop-color="#60a5fa" stop-opacity="0.20" />
          <stop offset="100%" stop-color="#0f172a" stop-opacity="0" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" :width="width" :height="height" fill="#020617" />
      <g opacity="0.45">
        <path v-for="x in gridX" :key="`vx-${x}`" :d="`M ${x} 0 V ${height}`" stroke="#1e293b" stroke-width="1" />
        <path v-for="y in gridY" :key="`vy-${y}`" :d="`M 0 ${y} H ${width}`" stroke="#1e293b" stroke-width="1" />
      </g>
      <path :d="areaPath" fill="url(#adsr-fill)" />
      <path :d="path" fill="none" stroke="#5eead4" stroke-width="3" stroke-linejoin="round" stroke-linecap="round" />
      <g opacity="0.8">
        <line v-for="marker in markers" :key="marker.key" :x1="marker.x" :y1="top" :x2="marker.x" :y2="bottom" :stroke="marker.stroke" stroke-dasharray="5 6" stroke-width="1.5" />
      </g>
      <g class="pointer-events-none select-none">
        <text v-for="label in labels" :key="label.key" :x="label.x" :y="label.y" text-anchor="middle" class="fill-slate-200 text-[13px] font-medium tracking-wide">
          {{ label.text }}
        </text>
      </g>
      <circle v-for="handle in handles" :key="handle.name" :cx="handle.x" :cy="handle.y" r="6" fill="#0f172a" stroke="#a5f3fc" stroke-width="2" class="cursor-grab" @pointerdown="startDrag($event, handle.name)" />
    </svg>
    <div class="grid grid-cols-2 gap-2">
      <label v-for="field in fields" :key="field.key" class="min-w-0 text-[11px] uppercase tracking-wide text-slate-500">
        <span class="block truncate">{{ field.label }}</span>
        <input class="mt-1 w-full rounded border border-slate-600 bg-slate-900 px-2 py-1 text-xs normal-case text-slate-100 outline-none focus:border-cyan-500" type="number" :min="field.min" :max="field.max" :step="field.step" :value="adsr[field.key]" @input="updateField(field.key, $event.target.value)" />
      </label>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { ADSR_MAX_TIME_SECONDS, buildAdsrGeometry, pointerToAdsr } from './synthAdsrGeometry.js'

const props = defineProps({ adsr: { type: Object, required: true } })
const emit = defineEmits(['update:adsr'])
const width = 420
const height = 208
const svgEl = ref(null)
const activeHandle = ref(null)
const geometry = computed(() => buildAdsrGeometry(props.adsr, width, height))
const path = computed(() => `M ${geometry.value.points.map(point => `${point.x} ${point.y}`).join(' L ')}`)
const areaPath = computed(() => `${path.value} L ${width - 14} ${height - 14} L 14 ${height - 14} Z`)
const handles = computed(() => [
  { name: 'attack', ...geometry.value.attack },
  { name: 'decay', ...geometry.value.decay },
  { name: 'release', ...geometry.value.release },
])
const top = computed(() => geometry.value.top)
const bottom = computed(() => geometry.value.bottom)
const gridX = computed(() => Array.from({ length: 7 }, (_, index) => 14 + ((width - 28) / 6) * index))
const gridY = computed(() => Array.from({ length: 5 }, (_, index) => 14 + ((height - 28) / 4) * index))
const markers = computed(() => [
  { key: 'attack', x: geometry.value.attack.x, stroke: '#2dd4bf' },
  { key: 'decay', x: geometry.value.decay.x, stroke: '#a78bfa' },
  { key: 'sustain', x: geometry.value.sustain.x, stroke: '#fb923c' },
  { key: 'release', x: geometry.value.release.x, stroke: '#38bdf8' },
])
const labels = computed(() => [
  { key: 'attack', x: (14 + geometry.value.attack.x) / 2, y: height - 18, text: 'Attack' },
  { key: 'decay', x: (geometry.value.attack.x + geometry.value.decay.x) / 2, y: height - 18, text: 'Decay' },
  { key: 'sustain', x: (geometry.value.decay.x + geometry.value.sustain.x) / 2, y: height - 18, text: 'Sustain' },
  { key: 'release', x: (geometry.value.sustain.x + geometry.value.release.x) / 2, y: height - 18, text: 'Release' },
])
const fields = [
  { key: 'attack', label: 'Attack (s)', min: 0, max: ADSR_MAX_TIME_SECONDS, step: 0.01 },
  { key: 'decay', label: 'Decay (s)', min: 0, max: ADSR_MAX_TIME_SECONDS, step: 0.01 },
  { key: 'sustain', label: 'Sustain', min: 0, max: 1, step: 0.01 },
  { key: 'release', label: 'Release (s)', min: 0, max: ADSR_MAX_TIME_SECONDS, step: 0.01 },
]

function startDrag(event, handle) {
  activeHandle.value = handle
  svgEl.value?.setPointerCapture(event.pointerId)
  updateFromPointer(event)
}

function moveHandle(event) {
  if (!activeHandle.value) return
  updateFromPointer(event)
}

function endDrag(event) {
  if (!activeHandle.value) return
  activeHandle.value = null
  svgEl.value?.releasePointerCapture(event.pointerId)
}

function updateFromPointer(event) {
  const rect = svgEl.value?.getBoundingClientRect()
  if (!rect) return
  const x = (event.clientX - rect.left) / rect.width * width
  const y = (event.clientY - rect.top) / rect.height * height
  emit('update:adsr', pointerToAdsr(activeHandle.value, x, y, props.adsr, width, height))
}

function updateField(key, value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return
  const max = key === 'sustain' ? 1 : ADSR_MAX_TIME_SECONDS
  emit('update:adsr', { ...props.adsr, [key]: Math.max(0, Math.min(max, number)) })
}
</script>
